import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, GitMerge, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import type { BlogCategory } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@/components/ui/confirm-provider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * BlogCategoryManagerDialog
 *
 * Create / rename / delete blog categories. The backend has always exposed full
 * CRUD at /projects/:projectId/categories, and auto-generated posts resolve a
 * category from the LLM's suggestion — but the admin had no way to DEFINE one,
 * so the picker in the post editor could only ever offer whatever autogeneration
 * happened to mint. This closes that loop.
 *
 * Category names double as an SEO signal (they surface as the post's topical
 * label and in category archive URLs), so they are worth curating deliberately
 * rather than leaving to whatever a model suggested.
 *
 * Delete is safe by construction: the backend unsets `categoryId` on every post
 * that referenced the deleted row, so posts fall back to "Uncategorized" rather
 * than holding a dangling id that renders as "Unknown".
 */

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Derive a URL-safe slug from a display name (mirrors the backend rule). */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

interface FormValues {
  name: string;
  slug: string;
  description: string;
}

export default function BlogCategoryManagerDialog({ projectId, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const {
    data: categoriesData,
    isPending,
    isFetching,
  } = useQuery({
    queryKey: qk.blogCategories(projectId),
    queryFn: () => apiHelpers.getBlogCategories(projectId),
    enabled: open && Boolean(projectId),
  });

  const categories: BlogCategory[] = categoriesData?.data?.data || [];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [mergingId, setMergingId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEditingId(null);
      setShowCreate(false);
      setMergingId(null);
    }
  }, [open]);

  /** Categories and the post picker both read this key — refresh both. */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.blogCategories(projectId) });
    queryClient.invalidateQueries({ queryKey: qk.blogPosts(projectId) });
  };

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      apiHelpers.createBlogCategory(projectId, {
        name: values.name,
        slug: values.slug || undefined,
        description: values.description || undefined,
      }),
    onSuccess: () => {
      toast.success('Category created');
      invalidate();
      setShowCreate(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not create the category')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: FormValues }) =>
      apiHelpers.updateBlogCategory(projectId, id, {
        name: values.name,
        slug: values.slug || undefined,
        description: values.description || undefined,
      }),
    onSuccess: () => {
      toast.success('Category updated');
      invalidate();
      setEditingId(null);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not update the category')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiHelpers.deleteBlogCategory(projectId, id),
    onSuccess: (res) => {
      const freed = (res?.data?.data as { postsUncategorized?: number })?.postsUncategorized ?? 0;
      toast.success(
        freed > 0
          ? `Category deleted — ${freed} ${freed === 1 ? 'post is' : 'posts are'} now Uncategorized`
          : 'Category deleted'
      );
      invalidate();
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not delete the category')),
  });

  const mergeMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      apiHelpers.mergeBlogCategory(projectId, sourceId, targetId),
    onSuccess: (res) => {
      const moved = (res?.data?.data as { postsMoved?: number })?.postsMoved ?? 0;
      toast.success(`Merged — ${moved} ${moved === 1 ? 'post' : 'posts'} moved`);
      invalidate();
      setMergingId(null);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not merge the category')),
  });

  const askDelete = async (cat: BlogCategory) => {
    const count = cat.postCount ?? 0;
    const ok = await confirm({
      title: `Delete "${cat.name}"?`,
      message:
        count > 0
          ? `${count} ${count === 1 ? 'post uses' : 'posts use'} this category. ${count === 1 ? 'It' : 'They'} will become Uncategorized — no posts are deleted.`
          : 'This category has no posts. It will be removed permanently.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) deleteMutation.mutate(cat.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage blog categories</DialogTitle>
          <DialogDescription>
            Categories group your posts and act as a topical keyword for SEO. Slugs appear in
            category URLs and must be unique within this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground text-sm">
              {isPending
                ? 'Loading…'
                : `${categories.length} ${categories.length === 1 ? 'category' : 'categories'}`}
              {!isPending && isFetching && ' · Refreshing…'}
            </div>
            {!showCreate && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                New category
              </Button>
            )}
          </div>

          {showCreate && (
            <CategoryForm
              mode="create"
              initial={{ name: '', slug: '', description: '' }}
              onCancel={() => setShowCreate(false)}
              onSubmit={(values) => createMutation.mutate(values)}
              submitting={createMutation.isPending}
            />
          )}

          {isPending ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="text-primary h-5 w-5 animate-spin" />
            </div>
          ) : categories.length === 0 && !showCreate ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-muted-foreground text-sm">
                No categories yet. Click <span className="font-medium">New category</span> to create
                your first one.
              </p>
            </div>
          ) : (
            <ul className="divide-y rounded-lg border">
              {categories.map((cat) => (
                <li key={cat.id} className="p-3">
                  {editingId === cat.id ? (
                    <CategoryForm
                      mode="edit"
                      initial={{
                        name: cat.name,
                        slug: cat.slug,
                        description: cat.description || '',
                      }}
                      onCancel={() => setEditingId(null)}
                      onSubmit={(values) => updateMutation.mutate({ id: cat.id, values })}
                      submitting={updateMutation.isPending}
                    />
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{cat.name}</span>
                          <code className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
                            /{cat.slug}
                          </code>
                          <span className="text-muted-foreground text-xs">
                            {cat.postCount ?? 0} {(cat.postCount ?? 0) === 1 ? 'post' : 'posts'}
                          </span>
                        </div>
                        {cat.description && (
                          <p className="text-muted-foreground mt-1 text-sm">{cat.description}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {categories.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Merge ${cat.name} into another category`}
                            onClick={() => setMergingId(mergingId === cat.id ? null : cat.id)}
                          >
                            <GitMerge className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${cat.name}`}
                          onClick={() => setEditingId(cat.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Delete ${cat.name}`}
                          disabled={deleteMutation.isPending}
                          onClick={() => askDelete(cat)}
                        >
                          <Trash2 className="text-destructive h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {mergingId === cat.id && (
                    <div className="bg-muted/40 mt-3 flex flex-wrap items-center gap-2 rounded-lg border p-3">
                      <span className="text-sm">
                        Move all posts from <span className="font-medium">{cat.name}</span> into:
                      </span>
                      <select
                        aria-label={`Merge ${cat.name} into`}
                        className="bg-background rounded-md border px-2 py-1.5 text-sm"
                        defaultValue=""
                        onChange={(e) => {
                          const targetId = e.target.value;
                          if (targetId) mergeMutation.mutate({ sourceId: cat.id, targetId });
                        }}
                        disabled={mergeMutation.isPending}
                      >
                        <option value="">Choose a category…</option>
                        {categories
                          .filter((other) => other.id !== cat.id)
                          .map((other) => (
                            <option key={other.id} value={other.id}>
                              {other.name}
                            </option>
                          ))}
                      </select>
                      <span className="text-muted-foreground text-xs">
                        “{cat.name}” is then removed. No posts are deleted.
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Inline create/edit form
// ============================================================================

function CategoryForm({
  mode,
  initial,
  onCancel,
  onSubmit,
  submitting,
}: {
  mode: 'create' | 'edit';
  initial: FormValues;
  onCancel: () => void;
  onSubmit: (values: FormValues) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState(initial.name);
  const [slug, setSlug] = useState(initial.slug);
  const [description, setDescription] = useState(initial.description);
  // Only auto-follow the name while the user hasn't hand-edited the slug, so
  // renaming an existing category never silently changes its live URL.
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const canSubmit = name.trim().length > 0 && !submitting;

  return (
    <form
      className="bg-muted/40 space-y-3 rounded-lg border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({ name: name.trim(), slug: effectiveSlug, description: description.trim() });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="cat-name">Name</Label>
          <Input
            id="cat-name"
            value={name}
            autoFocus
            placeholder="e.g. Growth"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cat-slug">Slug</Label>
          <Input
            id="cat-slug"
            value={effectiveSlug}
            placeholder="growth"
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cat-desc">Description (optional)</Label>
        <Textarea
          id="cat-desc"
          rows={2}
          value={description}
          placeholder="What this category covers — also used on category archive pages."
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          <X className="mr-1.5 h-4 w-4" />
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {submitting ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-4 w-4" />
          )}
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
